/**
 * Compose a confirmation.
 *
 * Server calls in the order of the state machine rather than one call hiding
 * it: a draft is created, its files are attached to the draft, then it is sent.
 * A draft is a real state with its own record, and the reference number is
 * only allocated at send.
 *
 * If a file is refused part-way, the draft and the files already uploaded are
 * kept and reused on the next attempt, so trying again does not leave a trail
 * of half-finished drafts — unless the subject, message, priority or plate
 * changed, since a draft cannot be edited and a new one is needed.
 *
 * The offices come from the addressing directory: every active office, the
 * ones added under Administration included. Nothing here is hard-coded.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { PRIORITIES, isPlateNumber, normalizePlateNumber, type OfficeType, type Priority } from '@lto/contracts';

import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { Button, Panel } from '../components/ui';
import { FilePicker } from '../components/Attachments';

interface DirectoryOffice {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly officeType: OfficeType;
}

interface Assignee {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly position: string | null;
}

const PRIORITY_HELP: Record<Priority, string> = {
  NORMAL: 'Response expected within 24 hours',
  PRIORITY: 'Response expected within 8 hours',
  URGENT: 'Response expected within 2 hours',
};

/** How the recipient list is grouped, in this order. */
const OFFICE_GROUPS: ReadonlyArray<{ type: OfficeType; label: string }> = [
  { type: 'MAIN', label: 'Regional office' },
  { type: 'DISTRICT', label: 'District offices' },
  { type: 'EXTENSION', label: 'Extension offices' },
  { type: 'OTHER', label: 'Other offices' },
];

interface DraftInProgress {
  readonly id: string;
  /** What the draft was created with; a change means a new draft. */
  readonly key: string;
  /** Files already on the draft, by the File they came from. */
  readonly uploaded: Map<File, string>;
}

export function Compose() {
  const navigate = useNavigate();
  const { user } = useSession();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<Priority>('NORMAL');
  const [plate, setPlate] = useState('');
  const [officeId, setOfficeId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const draft = useRef<DraftInProgress | null>(null);

  const offices = useQuery({
    queryKey: ['office-directory'],
    queryFn: () => api.get<DirectoryOffice[]>('/api/v1/offices/directory'),
  });

  // Assignees load only once an office is chosen, and only for that office.
  const assignees = useQuery({
    queryKey: ['office-assignees', officeId],
    queryFn: () => api.get<Assignee[]>(`/api/v1/offices/${officeId}/assignees`),
    enabled: officeId !== '',
  });

  const plateNormalised = normalizePlateNumber(plate);
  const plateProblem =
    plate.trim() !== '' && !isPlateNumber(plateNormalised)
      ? 'Enter a plate number such as ABC 1234, AB 12345 or 1234 AB.'
      : null;

  const send = useMutation({
    mutationFn: async () => {
      const key = JSON.stringify({ subject, body, priority, plate: plateNormalised });
      let current = draft.current;
      if (current === null || current.key !== key) {
        const created = await api.post<{ id: string }>('/api/v1/confirmations', {
          subject,
          body,
          priority,
          ...(plateNormalised === '' ? {} : { plateNumber: plateNormalised }),
        });
        current = { id: created.id, key, uploaded: new Map() };
        draft.current = current;
      }

      // Files taken off the list after a failed attempt come off the draft too.
      for (const [file, attachmentId] of [...current.uploaded]) {
        if (files.includes(file)) continue;
        await api.del(`/api/v1/attachments/${attachmentId}`);
        current.uploaded.delete(file);
      }

      for (const [index, file] of files.entries()) {
        if (current.uploaded.has(file)) continue;
        setProgress(`Uploading ${index + 1} of ${files.length}: ${file.name}`);
        const created = await api.upload<{ id: string }>(`/api/v1/confirmations/${current.id}/attachments`, file);
        current.uploaded.set(file, created.id);
      }

      setProgress('Sending');
      await api.post(`/api/v1/confirmations/${current.id}/send`, {
        recipients: [
          {
            officeId,
            // Empty means office-addressed: anyone in that office may handle it
            // (ASM-006).
            assignedUserId: assignedUserId === '' ? null : assignedUserId,
          },
        ],
      });

      draft.current = null;
      return current.id;
    },
    onSuccess: (id) => navigate(`/confirmations/${id}`),
    onError: (caught) => {
      setProgress(null);
      if (caught instanceof ApiError) {
        setFieldErrors(caught.fieldErrors);
        setError(caught.problem?.detail ?? 'Could not send this confirmation.');
      } else {
        setError('Could not reach the server. Check your connection and try again.');
      }
    },
  });

  // Offices the caller could address. Their own is excluded: this system is
  // for office-to-office correspondence, and a confirmation addressed to
  // yourself has no counterpart to reply.
  const addressable = (offices.data ?? []).filter((o) => o.id !== user?.officeId);

  const ready =
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    officeId !== '' &&
    plateProblem === null &&
    !send.isPending;

  const fieldStyle = {
    borderRadius: 'var(--radius-sm)',
    borderColor: 'var(--color-rule-strong)',
    background: 'var(--color-surface)',
    color: 'var(--color-ink)',
  } as const;

  return (
    <div className="mx-auto max-w-3xl space-y-3">
      <h1 className="text-lg font-semibold tracking-tight">New confirmation</h1>

      <Panel>
        <form
          className="space-y-4 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            setFieldErrors({});
            send.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
            <div>
              <label htmlFor="subject" className="mb-1 block text-sm font-medium">
                Subject
              </label>
              <input
                id="subject"
                required
                maxLength={300}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Document Verification"
                className="interactive w-full border px-2.5 py-2 text-base"
                style={fieldStyle}
              />
              {fieldErrors['subject'] !== undefined && (
                <p className="mt-1 text-xs" style={{ color: 'var(--color-danger)' }}>
                  {fieldErrors['subject']}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="plate" className="mb-1 block text-sm font-medium">
                Plate number <span style={{ color: 'var(--color-ink-subtle)' }}>(optional)</span>
              </label>
              <input
                id="plate"
                maxLength={20}
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                placeholder="ABC 1234"
                autoComplete="off"
                aria-describedby="plate-help"
                className="interactive reference w-full border px-2.5 py-2 text-base"
                style={fieldStyle}
              />
              <p
                id="plate-help"
                className="mt-1 text-xs"
                style={{
                  color:
                    plateProblem !== null || fieldErrors['plateNumber'] !== undefined
                      ? 'var(--color-danger)'
                      : 'var(--color-ink-subtle)',
                }}
              >
                {plateProblem ??
                  fieldErrors['plateNumber'] ??
                  'The owner can follow its progress on the Region V portal by plate number.'}
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="office" className="mb-1 block text-sm font-medium">
                Recipient office
              </label>
              <select
                id="office"
                required
                value={officeId}
                onChange={(e) => {
                  setOfficeId(e.target.value);
                  // The previous assignee belongs to the previous office.
                  setAssignedUserId('');
                }}
                className="interactive w-full border px-2.5 py-2 text-base"
                style={fieldStyle}
              >
                <option value="">
                  {offices.isPending ? 'Loading offices…' : 'Select an office'}
                </option>
                {OFFICE_GROUPS.map((group) => {
                  const members = addressable.filter((o) => o.officeType === group.type);
                  if (members.length === 0) return null;
                  return (
                    <optgroup key={group.type} label={group.label}>
                      {members.map((office) => (
                        <option key={office.id} value={office.id}>
                          {office.name} ({office.code})
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
              {offices.isSuccess && addressable.length === 0 && (
                <p className="mt-1 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                  No other active office is available to address.
                </p>
              )}
              {offices.isError && (
                <p className="mt-1 text-xs" style={{ color: 'var(--color-danger)' }}>
                  Could not load the offices.{' '}
                  <button type="button" className="underline" onClick={() => void offices.refetch()}>
                    Try again
                  </button>
                </p>
              )}
            </div>

            <div>
              <label htmlFor="assignee" className="mb-1 block text-sm font-medium">
                Assign to <span style={{ color: 'var(--color-ink-subtle)' }}>(optional)</span>
              </label>
              <select
                id="assignee"
                value={assignedUserId}
                disabled={officeId === '' || assignees.isPending}
                onChange={(e) => setAssignedUserId(e.target.value)}
                className="interactive w-full border px-2.5 py-2 text-base disabled:opacity-55"
                style={fieldStyle}
              >
                <option value="">Anyone in that office</option>
                {(assignees.data ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.firstName} {r.lastName}
                    {r.position === null ? '' : ` — ${r.position}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium">Priority</span>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Priority">
              {PRIORITIES.map((p) => (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={priority === p}
                  onClick={() => setPriority(p)}
                  className="interactive border px-3 py-1.5 text-sm font-medium"
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    background: priority === p ? 'var(--color-accent-subtle)' : 'var(--color-surface)',
                    borderColor: priority === p ? 'var(--color-accent)' : 'var(--color-rule-strong)',
                    color: priority === p ? 'var(--color-accent)' : 'var(--color-ink-muted)',
                  }}
                >
                  {p.charAt(0) + p.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
              {PRIORITY_HELP[priority]}. The deadline is set from when it is sent.
            </p>
          </div>

          <div>
            <label htmlFor="body" className="mb-1 block text-sm font-medium">
              Message
            </label>
            <textarea
              id="body"
              required
              rows={8}
              maxLength={20_000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Please confirm receipt of the attached documents."
              className="interactive w-full border px-2.5 py-2 text-base"
              style={{ ...fieldStyle, resize: 'vertical' }}
            />
            <p className="mt-1 text-xs tabular" style={{ color: 'var(--color-ink-subtle)' }}>
              {body.length.toLocaleString()} / 20,000
            </p>
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium">
              Attachments <span style={{ color: 'var(--color-ink-subtle)' }}>(optional)</span>
            </span>
            <FilePicker files={files} onChange={setFiles} disabled={send.isPending} />
          </div>

          {error !== null && (
            <div
              role="alert"
              className="animate-rise border px-2.5 py-2 text-sm"
              style={{
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-danger-subtle)',
                borderColor: 'var(--color-danger)',
                color: 'var(--color-danger)',
              }}
            >
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4" style={{ borderColor: 'var(--color-rule)' }}>
            {progress !== null && (
              <span className="mr-auto text-xs" role="status" style={{ color: 'var(--color-ink-subtle)' }}>
                {progress}…
              </span>
            )}
            <Button type="button" variant="ghost" onClick={() => navigate('/confirmations')}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={send.isPending} disabled={!ready}>
              {send.isPending ? 'Sending' : 'Send confirmation'}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
}
